import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Alert, Badge, Button, ConfigProvider, Empty, Flex, Grid, Input, Pagination, Popconfirm, Segmented, Select, Spin, Tag, Tooltip, Typography, message, theme } from "antd";
import {
  ArrowLeft,
  Camera,
  CameraOff,
  ChevronLeft,
  ChevronRight,
  Hand,
  History,
  LogIn,
  Mail,
  Maximize,
  Megaphone,
  Mic,
  MicOff,
  Minimize,
  PhoneOff,
  Users,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ExamPeriodMonitorState } from "@types";
import {
  useAdmitCandidates,
  useChangeCandidateState,
  useGetProctoringProgress,
  useGetProctoringSession,
  useSetAudioMode,
  type CandidateStateAction,
  type ProctoringCandidate,
  type ProctoringProgress,
} from "@hooks";
import { CandidateTile, candidateName, examStateTag } from "./CandidateTile";
import { CandidateMediaControls } from "./CandidateMediaControls";
import { CandidateAudio, CandidateVideo } from "./CandidateVideo";
import { ProctorAssignmentsDrawer } from "./ProctorAssignmentsDrawer";
import { InviteAssessorsModal } from "./InviteAssessorsModal";
import { AudioModeControl, AUDIO_MODE_UI } from "./AudioModeControl";
import { FocusExamPanel, isInExamState } from "./CandidateExamStatus";
import { effectiveAudio, myAudioMode, staffHears, type AudioMode, type EffectiveAudio } from "./audioMode";
import { useProctoringRoom, type ParticipantMedia } from "./useProctoringRoom";
import { useStaffTalk } from "./useStaffTalk";

type Filter = "all" | "connected" | "disconnected" | "help";
type Scope = "mine" | "all";
type Sort = "name" | "time" | "progress";
type TalkTarget = { kind: "one"; identity: string } | { kind: "all" } | null;
type Tone = "info" | "success" | "warning" | "error";

interface ActivityEntry {
  id: number;
  at: Date;
  identity?: string;
  text: string;
  tone: Tone;
}

const PAGE_SIZES = [4, 9, 16, 25, 36, 49, 100];
const groupStyle: CSSProperties = { display: "flex", flexDirection: "column", gap: 6, padding: "0 12px" };
const groupCaption: CSSProperties = { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 };

const Elapsed = ({ since }: { since: number }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const s = Math.max(0, Math.floor((now - since) / 1000));
  return (
    <span style={{ color: "#bfbfbf", fontVariantNumeric: "tabular-nums", fontSize: 13 }} data-testid="talk-elapsed">
      {Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}
    </span>
  );
};
const TONE_COLOR: Record<Tone, string> = { info: "#8c8c8c", success: "#52c41a", warning: "#fa8c16", error: "#ff4d4f" };

const beep = () => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") ctx.resume();
    [
      { at: 0, freq: 660 },
      { at: 0.18, freq: 880 },
      { at: 0.36, freq: 1100 },
    ].forEach(({ at, freq }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + at + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.25);
    });
  } catch {
    // Audio may still be blocked; the banner, pulsing tile and tab title show the request anyway.
  }
};

// Browsers only allow sound after a user gesture, so one shared context is unlocked on the first click in the wall.
let sharedAudioContext: AudioContext | null = null;
const getAudioContext = () => (sharedAudioContext ??= new AudioContext());
const unlockAudio = () => {
  getAudioContext().resume().catch(() => undefined);
};
const HELP_REMINDER_MS = 30000;

/// Seconds left from the last snapshot; good enough for ordering tiles between two polls.
const approxRemaining = (p?: ProctoringProgress) => {
  if (!p?.sync || !isInExamState(p.state)) return Number.POSITIVE_INFINITY;
  if (p.sync.isRunning && p.sync.endsAtUnixMs != null) return (p.sync.endsAtUnixMs - p.sync.serverNowUnixMs) / 1000;
  return p.sync.remainingSeconds ?? Number.POSITIVE_INFINITY;
};

interface Props {
  termId: string;
  /** Rendered inside the term monitoring page instead of its own page. */
  embedded?: boolean;
  onBack?: () => void;
}

/// Live proctoring wall shared by the term monitoring page (admins) and the standalone page (assessors, full screen).
export const ProctoringWall = ({ termId, embedded = false, onBack }: Props) => {
  const { t } = useTranslation();
  const wallRef = useRef<HTMLDivElement>(null);
  const screens = Grid.useBreakpoint();
  // Phones and narrow tablets: icon-only toolbar, at most two columns, scrolling grid, activity below.
  const isMobile = screens.md === false;

  const { data: session, isLoading, isError } = useGetProctoringSession(termId);
  const { data: progressList } = useGetProctoringProgress(termId);
  const { media, status, error, room, audio, speaking } = useProctoringRoom(session?.serverUrl, session?.token);
  const setAudioMode = useSetAudioMode(termId);
  const changeCandidateState = useChangeCandidateState(termId);
  const admit = useAdmitCandidates(termId);

  const [scope, setScope] = useState<Scope | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [sortBy, setSortBy] = useState<Sort>("name");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // Candidates the proctor listens to on purpose (needed when their audio mode is silence).
  const [listened, setListened] = useState<Set<string>>(() => new Set());
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [pendingState, setPendingState] = useState<{ id: string; action: CandidateStateAction } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [talkTo, setTalkTo] = useState<TalkTarget>(null);
  const [talkCamera, setTalkCamera] = useState(false);
  const [talkMic, setTalkMic] = useState(true);
  const [talkSince, setTalkSince] = useState(0);
  const [showActivity, setShowActivity] = useState(() => window.innerWidth >= 768);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [admittingId, setAdmittingId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const allCandidates = useMemo(() => session?.candidates ?? [], [session]);
  const myCount = allCandidates.filter((c) => c.assessorId === session?.currentUserId).length;
  // Admins who are also assessors see their own candidates first; they can still switch to everyone.
  const canChooseScope = !!session?.canManageAssignments && !!session?.isAssessor && myCount > 0;
  const effectiveScope: Scope = scope ?? (canChooseScope ? "mine" : "all");
  const candidates = useMemo(
    () => (effectiveScope === "mine" ? allCandidates.filter((c) => c.assessorId === session?.currentUserId) : allCandidates),
    [allCandidates, effectiveScope, session?.currentUserId]
  );
  const nameOf = useMemo(() => {
    const map: Record<string, string> = {};
    allCandidates.forEach((c) => (map[c.identity] = candidateName(c)));
    return map;
  }, [allCandidates]);
  const progressById = useMemo(() => {
    const map: Record<string, ProctoringProgress> = {};
    (progressList ?? []).forEach((p) => (map[p.examInstanceId] = p));
    return map;
  }, [progressList]);
  // The progress poll (10 s) is fresher than the session list (30 s).
  const stateOf = useCallback(
    (c: ProctoringCandidate) => progressById[c.examInstanceId]?.state ?? c.examInstanceState,
    [progressById]
  );
  // The candidate's player reports its phase at once; the exam state covers older players.
  const inExamOf = useCallback(
    (c: ProctoringCandidate) => {
      const phase = media[c.identity]?.phase;
      return phase === "exam" ? true : phase === "waiting" ? false : isInExamState(stateOf(c));
    },
    [media, stateOf]
  );

  const log = useCallback((text: string, tone: Tone, identity?: string) => {
    setActivity((list) => [{ id: Date.now() + Math.random(), at: new Date(), identity, text, tone }, ...list].slice(0, 300));
  }, []);

  // Turn room state changes into a readable activity log (who asked for help, who switched devices off, who left).
  const previous = useRef<Record<string, ParticipantMedia> | null>(null);
  useEffect(() => {
    if (status !== "connected") return;
    const before = previous.current;
    previous.current = media;
    if (!before) return;
    let newHelp = false;
    for (const c of candidates) {
      const was = before[c.identity];
      const now = media[c.identity];
      const name = nameOf[c.identity];
      if (!!was?.connected !== !!now?.connected) {
        log(now?.connected ? t("proctoring.evConnected", { name }) : t("proctoring.evDisconnected", { name }), now?.connected ? "info" : "warning", c.identity);
        continue;
      }
      if (!now?.connected || !was) continue;
      if (!was.help && now.help) {
        newHelp = true;
        log(t("proctoring.evHelp", { name }), "warning", c.identity);
      }
      if (was.help && !now.help) log(t("proctoring.evHelpCleared", { name }), "info", c.identity);
      if (was.cameraOn && now.cameraOn === false) log(t("proctoring.evCameraOff", { name }), "error", c.identity);
      if (was.cameraOn === false && now.cameraOn) log(t("proctoring.evCameraOn", { name }), "success", c.identity);
      if (was.micOn && now.micOn === false) log(t("proctoring.evMicOff", { name }), "warning", c.identity);
      if (was.micOn === false && now.micOn) log(t("proctoring.evMicOn", { name }), "success", c.identity);
    }
    if (newHelp) beep();
  }, [media, status, candidates, nameOf, log, t]);

  const connectedCount = candidates.filter((c) => media[c.identity]?.connected).length;
  const helpList = candidates.filter((c) => media[c.identity]?.connected && media[c.identity]?.help);
  const receivedVideos = Object.values(media).filter((m) => m.video).length;

  useEffect(() => {
    if (helpList.length === 0) return;
    const original = document.title;
    document.title = `(${helpList.length}) ✋ ${t("proctoring.helpRequested")}`;
    return () => {
      document.title = original;
    };
  }, [helpList.length, t]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = candidates.filter((c) => {
      const m = media[c.identity];
      if (filter === "connected" && !m?.connected) return false;
      if (filter === "disconnected" && m?.connected) return false;
      if (filter === "help" && !(m?.connected && m?.help)) return false;
      return !q || `${candidateName(c)} ${c.email ?? ""}`.toLowerCase().includes(q);
    });
    if (sortBy === "time") return [...list].sort((a, b) => approxRemaining(progressById[a.examInstanceId]) - approxRemaining(progressById[b.examInstanceId]));
    if (sortBy === "progress") {
      const pct = (c: ProctoringCandidate) => (isInExamState(stateOf(c)) ? progressById[c.examInstanceId]?.percentage ?? 0 : 101);
      return [...list].sort((a, b) => pct(a) - pct(b));
    }
    return list;
  }, [candidates, media, search, filter, sortBy, progressById, stateOf]);

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageItems = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const columns = Math.max(1, isMobile ? Math.min(2, pageItems.length) : Math.ceil(Math.sqrt(pageItems.length)));
  const rows = Math.max(1, Math.ceil(pageItems.length / columns));
  const compact = columns >= 6;

  const focusedIndex = visible.findIndex((c) => c.identity === focusedId);
  const focused = focusedIndex >= 0 ? visible[focusedIndex] : candidates.find((c) => c.identity === focusedId);
  const focusedMedia = focused ? media[focused.identity] : undefined;

  const talkTargets = useMemo(() => {
    if (talkTo?.kind === "one") return media[talkTo.identity]?.connected ? [talkTo.identity] : [];
    if (talkTo?.kind === "all") return candidates.filter((c) => media[c.identity]?.connected).map((c) => c.identity);
    return [];
  }, [talkTo, candidates, media]);
  useStaffTalk(room, talkTargets, talkMic, talkCamera);

  const startTalk = (identity: string) => {
    setTalkTo({ kind: "one", identity });
    setTalkMic(true);
    setTalkSince(Date.now());
    log(t("proctoring.evTalkStart", { name: nameOf[identity] }), "success", identity);
  };
  const stopTalk = () => {
    if (talkTo?.kind === "one") log(t("proctoring.evTalkStop", { name: nameOf[talkTo.identity] }), "info", talkTo.identity);
    if (talkTo?.kind === "all") log(t("proctoring.evAnnounceStop"), "info");
    setTalkTo(null);
    setTalkCamera(false);
    setTalkMic(true);
  };
  // The proctor's camera is opt-in: it can be shown to the candidate with or without speaking.
  const toggleMyCamera = (identity?: string) => {
    if (!talkTo && identity) {
      setTalkTo({ kind: "one", identity });
      setTalkMic(false);
      setTalkCamera(true);
      setTalkSince(Date.now());
      log(t("proctoring.evCameraShown", { name: nameOf[identity] }), "success", identity);
      return;
    }
    if (talkCamera && !talkMic) return stopTalk();
    setTalkCamera((v) => !v);
  };
  const toggleMyMic = () => {
    if (talkMic && !talkCamera) return stopTalk();
    setTalkMic((v) => !v);
  };
  const answerHelp = (identity: string) => {
    setFocusedId(identity);
    setListened((s) => new Set(s).add(identity));
    startTalk(identity);
  };
  const toggleListen = (identity: string) =>
    setListened((s) => {
      const next = new Set(s);
      if (!next.delete(identity)) next.add(identity);
      return next;
    });

  // Audio mode: an assessor (or an admin looking at "my candidates") sets it for their own candidates; an admin
  // looking at everyone sets it for the whole room. The wall plays every candidate the proctor should hear.
  const myStaffIdentity = `staff-${session?.currentUserId}`;
  const wholeRoom = !!session?.canManageAssignments && effectiveScope === "all";
  const myMode = myAudioMode(audio, myStaffIdentity, wholeRoom);
  const effectiveOf = useCallback((c: ProctoringCandidate) => effectiveAudio(audio, c.identity, inExamOf(c)), [audio, inExamOf]);
  const heardOf = (c: ProctoringCandidate) => !speakerMuted && staffHears(effectiveOf(c), listened.has(c.identity));
  const audioCounts = useMemo(() => {
    const counts: Record<EffectiveAudio, number> = { open: 0, listen: 0, muted: 0 };
    candidates.forEach((c) => media[c.identity]?.connected && counts[effectiveOf(c)]++);
    return counts;
  }, [candidates, media, effectiveOf]);
  const changeAudioMode = (mode: AudioMode) =>
    setAudioMode.mutate(
      { mode, scope: wholeRoom ? "all" : "mine" },
      {
        onSuccess: () => log(t("proctoring.evAudioMode", { mode: t(AUDIO_MODE_UI[mode].label) }), "info"),
        onError: (e: any) => message.error(typeof e?.response?.data === "string" ? e.response.data : t("proctoring.audioFailed")),
      }
    );

  const changeState = (c: ProctoringCandidate, action: CandidateStateAction) => {
    setPendingState({ id: c.examInstanceId, action });
    changeCandidateState.mutate(
      { examInstanceId: c.examInstanceId, action },
      {
        onSuccess: ({ changed }) => {
          if (!changed) return void message.info(t("proctoring.stateNotChanged"));
          const text = t(`proctoring.ev_${action}`, { name: candidateName(c) });
          message.success(text);
          log(text, action === "exclude" ? "error" : action === "pause" ? "warning" : "success", c.identity);
        },
        onError: () => message.error(t("proctoring.stateFailed")),
        onSettled: () => setPendingState(null),
      }
    );
  };

  // Letting candidates into the exam from the wall (same as confirming them on the monitoring page).
  const waitingList = candidates.filter((c) => stateOf(c) === ExamPeriodMonitorState.WaitingForParticipant);
  const stateCounts = useMemo(() => {
    let inExam = 0;
    let done = 0;
    candidates.forEach((c) => {
      const s = stateOf(c);
      if (isInExamState(s)) inExam++;
      else if (s === ExamPeriodMonitorState.Completed || s === ExamPeriodMonitorState.Excluded) done++;
    });
    return { inExam, done };
  }, [candidates, stateOf]);
  const admitCandidates = (list: ProctoringCandidate[]) => {
    if (list.length === 0) return;
    setAdmittingId(list.length === 1 ? list[0].examInstanceId : "all");
    admit.mutate(
      list.map((c) => c.examInstanceId),
      {
        onSuccess: ({ admitted }) => {
          if (admitted === 0) return void message.info(t("proctoring.admitNone"));
          message.success(t("proctoring.admitDone", { count: admitted }));
          if (list.length === 1) log(t("proctoring.evAdmitted", { name: candidateName(list[0]) }), "success", list[0].identity);
          else log(t("proctoring.evAdmittedAll", { count: admitted }), "success");
        },
        onError: () => message.error(t("proctoring.admitFailed")),
        onSettled: () => setAdmittingId(null),
      }
    );
  };

  const label = (text: string) => (isMobile ? null : text);

  const helpCountRef = useRef(0);
  helpCountRef.current = helpList.length;
  useEffect(() => {
    if (helpList.length === 0) return;
    const timer = window.setInterval(() => helpCountRef.current > 0 && beep(), HELP_REMINDER_MS);
    return () => window.clearInterval(timer);
  }, [helpList.length > 0]);

  const step = useCallback(
    (delta: number) => {
      if (visible.length === 0 || focusedIndex < 0) return;
      setFocusedId(visible[(focusedIndex + delta + visible.length) % visible.length].identity);
    },
    [visible, focusedIndex]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!focusedId || (e.target as HTMLElement)?.tagName === "INPUT") return;
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "Escape") setFocusedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedId, step]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wallRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else wallRef.current?.requestFullscreen();
  };

  if (isLoading) return <Spin style={{ display: "block", marginTop: 80 }} />;
  if (isError || !session) return <Alert type="error" showIcon message={t("proctoring.connectionError")} />;

  const focusedName = focused ? candidateName(focused) : "";
  const talkingToFocused = talkTo?.kind === "one" && talkTo.identity === focused?.identity;
  // Whoever is already being talked to no longer needs an "Answer" button.
  const helpPending = helpList.filter((c) => !talkTargets.includes(c.identity));
  // On a phone the focused candidate gets the screen; list controls only matter on the grid.
  const focusMobile = isMobile && !!focused;
  const groupSeparator: CSSProperties = isMobile ? { borderTop: "1px solid #303030", paddingTop: 10 } : {};
  const groupBox: CSSProperties = isMobile ? groupStyle : { ...groupStyle, background: "#1d1d1d", borderRadius: 6, padding: "8px 12px" };

  const callBar = talkingToFocused && (
    <Flex
      align="center"
      gap={isMobile ? 6 : 10}
      data-testid="talk-callbar"
      style={{
        position: "absolute",
        left: "50%",
        bottom: isMobile ? 8 : 16,
        transform: "translateX(-50%)",
        maxWidth: "calc(100% - 16px)",
        whiteSpace: "nowrap",
        background: "rgba(16,16,16,0.85)",
        backdropFilter: "blur(6px)",
        border: "1px solid #2251ff",
        borderRadius: 999,
        padding: isMobile ? "4px 4px 4px 12px" : "6px 8px 6px 16px",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: "#2251ff", animation: "proctorTalkPulse 1.4s infinite" }} />
      {!isMobile && (
        <Typography.Text strong ellipsis style={{ color: "#fff" }}>
          {t("proctoring.talkingTo", { name: focusedName })}
        </Typography.Text>
      )}
      <Elapsed since={talkSince} />
      <Tooltip title={talkMic ? t("proctoring.myMicOff") : t("proctoring.myMicOn")}>
        <Button shape="circle" size="large" danger={!talkMic} icon={talkMic ? <Mic size={18} /> : <MicOff size={18} />} onClick={toggleMyMic} data-testid="my-mic-toggle" />
      </Tooltip>
      <Tooltip title={talkCamera ? t("proctoring.myCameraOff") : t("proctoring.myCameraOn")}>
        <Button
          shape="circle"
          size="large"
          type={talkCamera ? "primary" : "default"}
          icon={talkCamera ? <Camera size={18} /> : <CameraOff size={18} />}
          onClick={() => toggleMyCamera()}
          data-testid="my-camera-toggle"
        />
      </Tooltip>
      <Tooltip title={t("proctoring.stopTalking")}>
        <Button shape="circle" size="large" danger type="primary" icon={<PhoneOff size={18} />} onClick={stopTalk} data-testid="talk-stop" />
      </Tooltip>
    </Flex>
  );
  const focusNav = (
    <Flex gap={8} justify={isMobile ? "space-between" : undefined}>
      {isMobile && <Button icon={<ChevronLeft size={16} />} onClick={() => step(-1)} />}
      <Button icon={<ArrowLeft size={16} />} onClick={() => setFocusedId(null)}>
        {t("proctoring.backToGrid")}
      </Button>
      {isMobile && <Button icon={<ChevronRight size={16} />} onClick={() => step(1)} />}
    </Flex>
  );

  return (
    <div
      ref={wallRef}
      data-testid="proctoring-wall"
      data-received-videos={receivedVideos}
      data-audio-mode={myMode}
      onPointerDown={unlockAudio}
      style={{
        display: "flex",
        flexDirection: "column",
        height: isFullscreen ? "100vh" : isMobile ? "calc(100dvh - 96px)" : embedded ? "calc(100vh - 230px)" : "calc(100vh - 120px)",
        minHeight: isMobile ? 420 : 520,
        background: "#0b0b0b",
        padding: isMobile ? 4 : 8,
        gap: isMobile ? 6 : 8,
        borderRadius: isFullscreen ? 0 : 8,
        // The app's side menu takes half of a phone screen, so the standalone wall covers the whole viewport there.
        ...(isMobile && !embedded && !isFullscreen
          ? { position: "fixed", inset: 0, zIndex: 999, height: "100dvh", minHeight: 0, borderRadius: 0 }
          : {}),
      }}
    >
      <style>{`@keyframes proctorHelpPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(250,140,22,0); } 50% { box-shadow: 0 0 0 6px rgba(250,140,22,0.6); } }
@keyframes proctorTalkPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
      {/* Every control on the black wall uses the dark theme, so disabled and secondary controls stay readable. */}
      <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>

      <Flex gap={isMobile ? 6 : 8} align="center" wrap="wrap" data-testid="wall-toolbar">
        {onBack && (
          <Tooltip title={t("proctoring.back")}>
            <Button icon={<ArrowLeft size={16} />} onClick={onBack} />
          </Tooltip>
        )}
        {!embedded && (
          <Typography.Text strong ellipsis style={{ color: "#fff", fontSize: isMobile ? 14 : 16, maxWidth: isMobile ? "60vw" : undefined }}>
            {session.examTitle}
          </Typography.Text>
        )}
        <Tag color={status === "connected" ? "green" : status === "error" ? "red" : "default"} data-testid="proctoring-status">
          {t("proctoring.connected")}: {connectedCount}/{candidates.length}
        </Tag>
        {!isMobile && (stateCounts.inExam > 0 || waitingList.length > 0 || stateCounts.done > 0) && (
          <Typography.Text style={{ color: "#bfbfbf", fontSize: 12 }} data-testid="state-summary">
            {[
              stateCounts.inExam > 0 && t("proctoring.summaryInExam", { count: stateCounts.inExam }),
              waitingList.length > 0 && t("proctoring.summaryWaiting", { count: waitingList.length }),
              stateCounts.done > 0 && t("proctoring.summaryDone", { count: stateCounts.done }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography.Text>
        )}
        {canChooseScope && (
          <Segmented<Scope>
            value={effectiveScope}
            onChange={(v) => {
              setScope(v);
              setPage(1);
            }}
            options={[
              { value: "mine", label: `${t("proctoring.scopeMine")} (${myCount})` },
              { value: "all", label: `${t("proctoring.scopeAll")} (${allCandidates.length})` },
            ]}
            data-testid="proctoring-scope"
          />
        )}
        {!focusMobile && (
        <>
        <Input.Search
          allowClear
          value={search}
          placeholder={t("proctoring.search")}
          style={{ width: isMobile ? "100%" : 200 }}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Segmented<Filter>
          value={filter}
          size={isMobile ? "small" : "middle"}
          onChange={(v) => {
            setFilter(v);
            setPage(1);
          }}
          options={[
            { value: "all", label: t("proctoring.filterAll") },
            { value: "connected", label: isMobile ? <Badge status="success" /> : t("proctoring.filterConnected") },
            { value: "disconnected", label: isMobile ? <Badge status="default" /> : t("proctoring.filterDisconnected") },
            {
              value: "help",
              label: (
                <span data-testid="filter-help" style={{ color: helpList.length > 0 ? "#fa8c16" : undefined }}>
                  <Hand size={12} /> {!isMobile && t("proctoring.filterHelp")} ({helpList.length})
                </span>
              ),
            },
          ]}
        />
        {!isMobile && (
          <Select
            value={pageSize}
            style={{ width: 130 }}
            onChange={(v) => {
              setPageSize(v);
              setPage(1);
            }}
            options={PAGE_SIZES.map((n) => ({ value: n, label: n === 100 ? t("proctoring.all") : `${n} ${t("proctoring.perPage")}` }))}
          />
        )}
        <Select<Sort>
          value={sortBy}
          size={isMobile ? "small" : "middle"}
          style={{ width: isMobile ? 130 : 170 }}
          onChange={setSortBy}
          options={[
            { value: "name", label: t("proctoring.sortName") },
            { value: "time", label: t("proctoring.sortTime") },
            { value: "progress", label: t("proctoring.sortProgress") },
          ]}
          data-testid="wall-sort"
        />
        <Pagination simple size={isMobile ? "small" : undefined} current={currentPage} total={visible.length} pageSize={pageSize} onChange={setPage} />
        </>
        )}
        <Flex gap={isMobile ? 6 : 8} wrap="wrap" style={{ marginLeft: "auto" }}>
          {waitingList.length > 0 && (
            <Popconfirm
              title={t("proctoring.admitAllConfirm", { count: waitingList.length })}
              onConfirm={() => admitCandidates(waitingList)}
              getPopupContainer={() => wallRef.current ?? document.body}
            >
              <Tooltip title={isMobile ? t("proctoring.admitAll", { count: waitingList.length }) : undefined}>
                <Button type="primary" icon={<LogIn size={16} />} loading={admittingId === "all"} data-testid="admit-all">
                  {isMobile ? waitingList.length : t("proctoring.admitAll", { count: waitingList.length })}
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
          <AudioModeControl
            mode={myMode}
            wholeRoom={wholeRoom}
            loading={setAudioMode.isPending}
            disabled={!room || connectedCount === 0}
            iconOnly={isMobile}
            onChange={changeAudioMode}
            getPopupContainer={() => wallRef.current ?? document.body}
          />
          <Tooltip title={isMobile ? t("proctoring.talkToAll") : undefined}>
            <Button
              icon={<Megaphone size={16} />}
              disabled={!room || connectedCount === 0 || talkTo?.kind === "all"}
              onClick={() => {
                setTalkTo({ kind: "all" });
                log(t("proctoring.evAnnounceStart", { count: connectedCount }), "success");
              }}
              data-testid="talk-all"
            >
              {label(t("proctoring.talkToAll"))}
            </Button>
          </Tooltip>
          {session.canManageAssignments && (
            <Tooltip title={isMobile ? t("proctoring.assignments") : undefined}>
              <Button icon={<Users size={16} />} onClick={() => setAssignOpen(true)} data-testid="open-assignments">
                {label(t("proctoring.assignments"))}
              </Button>
            </Tooltip>
          )}
          {session.canManageAssignments && (
            <Tooltip title={isMobile ? t("proctoring.invite") : undefined}>
              <Button icon={<Mail size={16} />} onClick={() => setInviteOpen(true)} data-testid="invite-assessors">
                {label(t("proctoring.invite"))}
              </Button>
            </Tooltip>
          )}
          <Tooltip title={t("proctoring.activity")}>
            <Badge dot={!showActivity && activity.length > 0}>
              <Button icon={<History size={16} />} type={showActivity ? "primary" : "default"} onClick={() => setShowActivity((v) => !v)} />
            </Badge>
          </Tooltip>
          <Tooltip title={isMobile ? (isFullscreen ? t("proctoring.exitFullscreen") : t("proctoring.fullscreen")) : undefined}>
            <Button icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />} onClick={toggleFullscreen}>
              {label(isFullscreen ? t("proctoring.exitFullscreen") : t("proctoring.fullscreen"))}
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {error && <Alert type="error" showIcon message={`${t("proctoring.connectionError")}: ${error}`} />}

      {connectedCount > 0 && (
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          data-testid="audio-status"
          style={{ background: "#1a1a1a", border: `1px solid ${AUDIO_MODE_UI[myMode].color}55`, borderRadius: 6, padding: "4px 10px", color: "#d9d9d9", fontSize: 12 }}
        >
          {(() => {
            const ModeIcon = AUDIO_MODE_UI[myMode].icon;
            return <ModeIcon size={16} color={AUDIO_MODE_UI[myMode].color} />;
          })()}
          <Typography.Text strong style={{ color: "#fff", fontSize: 12 }}>
            {t(AUDIO_MODE_UI[myMode].label)}
          </Typography.Text>
          {audioCounts.open > 0 && <span>{t("proctoring.audioStatusOpen", { count: audioCounts.open })}</span>}
          {audioCounts.listen > 0 && <span>{t("proctoring.audioStatusListen", { count: audioCounts.listen })}</span>}
          {audioCounts.muted > 0 && <span>{t("proctoring.audioStatusMuted", { count: audioCounts.muted })}</span>}
          {listened.size > 0 && (
            <Button size="small" type="link" style={{ padding: 0, height: "auto" }} onClick={() => setListened(new Set())}>
              {t("proctoring.stopListeningAll", { count: listened.size })}
            </Button>
          )}
          <Tooltip title={speakerMuted ? t("proctoring.speakerOn") : t("proctoring.speakerOff")}>
            <Button
              size="small"
              style={{ marginLeft: "auto" }}
              danger={speakerMuted}
              icon={speakerMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              onClick={() => setSpeakerMuted((v) => !v)}
              data-testid="speaker-toggle"
            >
              {!isMobile && (speakerMuted ? t("proctoring.speakerMuted") : t("proctoring.speakerOnShort"))}
            </Button>
          </Tooltip>
        </Flex>
      )}
      {/* The wall plays every candidate the proctor should hear in the current audio mode (and those listened to on purpose). */}
      {candidates.map((c) => {
        const track = media[c.identity]?.audio;
        return track && heardOf(c) ? <CandidateAudio key={c.identity} track={track} /> : null;
      })}

      {helpPending.length > 0 && (
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          data-testid="help-banner"
          style={{ background: "#fa8c16", color: "#000", borderRadius: 6, padding: isMobile ? "4px 8px" : "6px 10px" }}
        >
          <Hand size={18} />
          <Typography.Text strong>{t("proctoring.helpBanner", { count: helpPending.length })}</Typography.Text>
          {helpPending.map((c) => (
            <Button key={c.identity} size="small" icon={<Mic size={14} />} onClick={() => answerHelp(c.identity)} data-testid="help-answer">
              {t("proctoring.answer", { name: candidateName(c) })}
            </Button>
          ))}
        </Flex>
      )}

      {talkTo && !talkingToFocused && (
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          data-testid="talk-banner"
          style={{ background: "#2251ff", color: "#fff", borderRadius: 6, padding: "6px 10px" }}
        >
          {talkTo.kind === "one" ? <Mic size={18} /> : <Megaphone size={18} />}
          <Typography.Text strong style={{ color: "#fff" }}>
            {talkTo.kind === "one"
              ? t("proctoring.talkingTo", { name: nameOf[talkTo.identity] })
              : t("proctoring.announcingTo", { count: talkTargets.length })}
          </Typography.Text>
          {talkTargets.length === 0 && <Tag color="red">{t("proctoring.notConnected")}</Tag>}
          <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
            <Button
              size="small"
              icon={talkMic ? <Mic size={14} /> : <MicOff size={14} />}
              onClick={toggleMyMic}
              data-testid="my-mic-toggle"
            >
              {talkMic ? t("proctoring.myMicOff") : t("proctoring.myMicOn")}
            </Button>
            <Button
              size="small"
              icon={talkCamera ? <Camera size={14} /> : <CameraOff size={14} />}
              onClick={() => toggleMyCamera()}
              data-testid="my-camera-toggle"
            >
              {talkCamera ? t("proctoring.myCameraOff") : t("proctoring.myCameraOn")}
            </Button>
          </ConfigProvider>
          <Button size="small" danger type="primary" icon={<PhoneOff size={14} />} onClick={stopTalk} data-testid="talk-stop">
            {talkTo.kind === "all" ? t("proctoring.stopAnnouncing") : t("proctoring.stopTalking")}
          </Button>
        </Flex>
      )}

      <Flex style={{ flex: 1, minHeight: 0 }} gap={8} vertical={isMobile}>
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {focused ? (
            <Flex style={{ flex: 1, minHeight: 0 }} gap={isMobile ? 0 : 8}>
              {!isMobile && (
                <Button type="text" icon={<ChevronLeft color="#fff" />} onClick={() => step(-1)} style={{ alignSelf: "center" }} />
              )}
              <Flex vertical gap={isMobile ? 6 : 8} style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              {isMobile && focusNav}
              <div
                style={{
                  position: "relative",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#000",
                  ...(isMobile ? { flex: "none", height: "min(48dvh, 75vw)" } : { flex: 1, minHeight: 0 }),
                  ...(talkingToFocused ? { boxShadow: "inset 0 0 0 2px #2251ff" } : {}),
                }}
                data-testid="proctor-focus"
              >
                {focusedMedia?.video && focusedMedia.cameraOn !== false ? (
                  <CandidateVideo track={focusedMedia.video} fit="contain" />
                ) : (
                  <Flex align="center" justify="center" style={{ height: "100%", color: "#8c8c8c" }}>
                    {!focusedMedia?.connected
                      ? t("proctoring.notConnected")
                      : focusedMedia.cameraOn === false
                        ? t("proctoring.cameraOff")
                        : t("proctoring.noVideo")}
                  </Flex>
                )}
                <Flex
                  align="center"
                  gap={6}
                  wrap="wrap"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    padding: isMobile ? "6px 8px 16px" : "10px 12px 24px",
                    background: "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)",
                  }}
                >
                  <Typography.Text style={{ color: "#fff", fontSize: isMobile ? 15 : 20, whiteSpace: "nowrap" }} strong>
                    {focusedName}
                  </Typography.Text>
                  {examStateTag(stateOf(focused), t)}
                  {focused.assessorName && !isMobile && <Tag>{t("proctoring.assessor")}: {focused.assessorName}</Tag>}
                  {focusedMedia?.connected && focusedMedia.cameraOn === false && <Tag color="red">{t("proctoring.cameraOff")}</Tag>}
                  {focusedMedia?.connected && focusedMedia.micOn === false && <Tag color="red">{t("proctoring.micOff")}</Tag>}
                  {focusedMedia?.help && !talkingToFocused && <Tag color="orange">{t("proctoring.helpRequested")}</Tag>}
                  {speaking.has(focused.identity) && focusedMedia?.micOn !== false && (
                    <Tag color="green">{heardOf(focused) ? t("proctoring.speaking") : t("proctoring.speakingNotHeard")}</Tag>
                  )}
                </Flex>
                {callBar}
              </div>
              <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
                <Flex
                  vertical={isMobile}
                  align="stretch"
                  gap={isMobile ? 10 : 8}
                  wrap={isMobile ? undefined : "wrap"}
                  data-testid="focus-actions"
                  style={{
                    background: "#141414",
                    borderRadius: 8,
                    padding: isMobile ? "10px 0" : 8,
                    ...(isMobile ? { flex: 1, minHeight: 0, overflowY: "auto" as const } : {}),
                  }}
                >
                  {!talkingToFocused && (
                  <div style={groupBox}>
                    <Typography.Text type="secondary" style={groupCaption}>
                      {t("proctoring.groupConversation")}
                    </Typography.Text>
                    <Flex gap={8} wrap="wrap">
                      <Button
                        type="primary"
                        danger={talkingToFocused}
                        icon={talkingToFocused ? <PhoneOff size={16} /> : <Mic size={16} />}
                        disabled={!room || !focusedMedia?.connected || talkTo?.kind === "all"}
                        onClick={() => (talkingToFocused ? stopTalk() : startTalk(focused.identity))}
                        data-testid="talk-focused"
                      >
                        {talkingToFocused ? t("proctoring.stopTalking") : t("proctoring.talk")}
                      </Button>
                      <Tooltip title={t("proctoring.myCameraHint")}>
                        <Button
                          icon={talkingToFocused && talkCamera ? <Camera size={16} /> : <CameraOff size={16} />}
                          disabled={!room || !focusedMedia?.connected || talkTo?.kind === "all" || (!!talkTo && !talkingToFocused)}
                          onClick={() => toggleMyCamera(focused.identity)}
                          data-testid="focus-my-camera"
                        >
                          {talkingToFocused && talkCamera ? t("proctoring.myCameraOff") : t("proctoring.myCameraOn")}
                        </Button>
                      </Tooltip>
                      {effectiveOf(focused) === "muted" && (
                      <Tooltip title={!focusedMedia?.audio ? t("proctoring.listenUnavailable") : undefined}>
                        <Button
                          icon={heardOf(focused) ? <Volume2 size={16} /> : <VolumeX size={16} />}
                          type={listened.has(focused.identity) ? "primary" : "default"}
                          disabled={!focusedMedia?.audio}
                          onClick={() => toggleListen(focused.identity)}
                          data-testid="focus-listen"
                        >
                          {listened.has(focused.identity) ? t("proctoring.stopListeningShort") : t("proctoring.listenShort")}
                        </Button>
                      </Tooltip>
                      )}
                    </Flex>
                  </div>
                  )}
                  <div style={{ ...groupBox, ...(talkingToFocused ? {} : groupSeparator) }}>
                    <Typography.Text type="secondary" style={groupCaption}>
                      {t("proctoring.groupDevices")}
                    </Typography.Text>
                    {focusedMedia?.connected ? (
                      <CandidateMediaControls
                        examTermId={termId}
                        examInstanceId={focused.examInstanceId}
                        candidateName={focusedName}
                        media={focusedMedia}
                        size="middle"
                        showLabels
                        onSent={(done) => log(`${t("proctoring.you")} → ${focusedName}: ${done}`, "info", focused.identity)}
                      />
                    ) : (
                      <Typography.Text type="secondary">{t("proctoring.notConnected")}</Typography.Text>
                    )}
                  </div>
                  <div style={{ ...groupBox, ...groupSeparator }}>
                    <Typography.Text type="secondary" style={groupCaption}>
                      {t("proctoring.groupExam")}
                    </Typography.Text>
                    <FocusExamPanel
                      progress={progressById[focused.examInstanceId]}
                      state={stateOf(focused)}
                      candidateName={focusedName}
                      pendingAction={
                        admittingId === focused.examInstanceId
                          ? "admit"
                          : pendingState?.id === focused.examInstanceId
                            ? pendingState.action
                            : null
                      }
                      onAdmit={() => admitCandidates([focused])}
                      onAction={(action) => changeState(focused, action)}
                      getPopupContainer={() => wallRef.current ?? document.body}
                    />
                  </div>
                  {!isMobile && (
                    <div style={{ ...groupStyle, marginLeft: "auto", alignItems: "flex-end" }}>
                      <Typography.Text type="secondary" style={groupCaption}>
                        {t("proctoring.focusHint")}
                      </Typography.Text>
                      {focusNav}
                    </div>
                  )}
                </Flex>
              </ConfigProvider>
            </Flex>
            {!isMobile && (
              <Button type="text" icon={<ChevronRight color="#fff" />} onClick={() => step(1)} style={{ alignSelf: "center" }} />
            )}
            </Flex>
          ) : pageItems.length === 0 ? (
            <Empty description={<span style={{ color: "#8c8c8c" }}>{t("proctoring.noCandidates")}</span>} style={{ marginTop: 80 }} />
          ) : (
            <div
              data-testid="proctor-grid"
              style={{
                flex: 1,
                minHeight: 0,
                display: "grid",
                gap: 6,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                // On phones tiles keep a usable size and the grid scrolls instead of shrinking them.
                ...(isMobile
                  ? { gridAutoRows: columns === 1 ? "min(56vw, 260px)" : "min(36vw, 200px)", overflowY: "auto" as const }
                  : { gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }),
              }}
            >
              {pageItems.map((c) => (
                <CandidateTile
                  key={c.identity}
                  examTermId={termId}
                  candidate={c}
                  media={media[c.identity]}
                  compact={compact || isMobile}
                  showAssessor={session.canManageAssignments && effectiveScope === "all"}
                  talking={talkTargets.includes(c.identity)}
                  onClick={() => setFocusedId(c.identity)}
                  state={stateOf(c)}
                  progress={progressById[c.examInstanceId]}
                  speaking={speaking.has(c.identity)}
                  heard={heardOf(c)}
                  listened={listened.has(c.identity)}
                  onToggleListen={effectiveOf(c) === "muted" ? () => toggleListen(c.identity) : undefined}
                  onAction={(done) => log(`${t("proctoring.you")} → ${candidateName(c)}: ${done}`, "info", c.identity)}
                  onAdmit={() => admitCandidates([c])}
                  admitting={admittingId === c.examInstanceId}
                />
              ))}
            </div>
          )}
        </div>

        {showActivity && (
          <div
            data-testid="activity-panel"
            style={{
              width: isMobile ? "100%" : 290,
              maxHeight: isMobile ? "30%" : undefined,
              flex: "none",
              background: "#141414",
              borderRadius: 8,
              padding: 8,
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            <Typography.Text strong style={{ color: "#fff" }}>
              {t("proctoring.activity")}
            </Typography.Text>
            {activity.length === 0 && (
              <Typography.Paragraph style={{ color: "#8c8c8c", marginTop: 8 }}>{t("proctoring.noActivity")}</Typography.Paragraph>
            )}
            {activity.map((a) => (
              <div
                key={a.id}
                onClick={() => a.identity && setFocusedId(a.identity)}
                style={{
                  cursor: a.identity ? "pointer" : "default",
                  borderLeft: `3px solid ${TONE_COLOR[a.tone]}`,
                  padding: "4px 8px",
                  marginTop: 6,
                  color: "#e6e6e6",
                  fontSize: 12,
                }}
              >
                <span style={{ color: "#8c8c8c", marginRight: 6 }}>{dayjs(a.at).format("HH:mm:ss")}</span>
                {a.text}
              </div>
            ))}
          </div>
        )}
      </Flex>

      </ConfigProvider>

      {/* In fullscreen only the wall element is painted, so the drawer must be mounted inside it. */}
      {session.canManageAssignments && (
        <InviteAssessorsModal
          termId={termId}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          getContainer={isFullscreen ? () => wallRef.current ?? document.body : undefined}
        />
      )}
      {session.canManageAssignments && (
        <ProctorAssignmentsDrawer
          examTermId={termId}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          getContainer={isFullscreen ? () => wallRef.current ?? document.body : undefined}
        />
      )}
    </div>
  );
};
